import { useProjectStream } from '../api/projectStream.js';
import { ingestProjectEvent, useQuoteJobsStore } from '../stores/quoteJobsStore.js';

/**
 * Trabajos en curso (presupuesto y análisis de precios): la parte que NO se ve.
 *
 * Este archivo se llamaba `QuoteJobPill.tsx` y su pieza principal era un pill con
 * anillo de avance en la barra superior. El pill se retiró (2026-08-23): su clic
 * llevaba a la pantalla vieja de análisis de precios, que ya no se usa —hoy todo
 * pasa dentro del presupuesto—. Se aceptó perder el aviso de "hay algo corriendo"
 * mientras estás en otra pantalla: el avance se ve entrando al presupuesto.
 *
 * Lo que quedó es lo que hace falta para que ese avance exista:
 *  - `QuoteJobsWatcher`, montado en `App`, mantiene abierto el WebSocket de cada
 *    proyecto con trabajo activo aunque el usuario navegue fuera, y con eso
 *    alimenta `quoteJobsStore` (de donde el presupuesto saca % y tiempo estimado).
 *  - `formatEta`, que usa la animación de progreso del presupuesto.
 */

/** Formatea el tiempo restante estimado para mostrárselo a una persona. */
export function formatEta(ms: number | null): string | null {
  if (ms == null || ms <= 0) return null;
  const s = Math.round(ms / 1000);
  if (s < 60) return `~${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `~${m} min`;
  return `~${Math.round(m / 60)} h`;
}

function ProjectWatch({ projectId }: { projectId: string }) {
  useProjectStream({
    projectId,
    onEvent: (ev) => ingestProjectEvent(projectId, ev),
  });
  return null;
}

export function QuoteJobsWatcher() {
  // Selector devuelve un string estable (ids ordenados) para no re-renderizar
  // en cada evento de progreso; solo cuando cambia el CONJUNTO de proyectos.
  const idsCsv = useQuoteJobsStore((s) => {
    const ids = new Set<string>();
    for (const j of Object.values(s.jobs)) {
      // Solo la empresa activa: no abrir WS a proyectos de otra empresa.
      if (j.status === 'running' && (!s.tenantId || j.tenantId === s.tenantId)) {
        ids.add(j.projectId);
      }
    }
    return Array.from(ids).sort().join(',');
  });
  const ids = idsCsv ? idsCsv.split(',') : [];
  return (
    <>
      {ids.map((id) => (
        <ProjectWatch key={id} projectId={id} />
      ))}
    </>
  );
}
